import { useState } from "react";
import { ArrowRight, X } from "lucide-react";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import type { DonationCampaign } from "../../donation/types";

type StoryPhase = "before" | "after";

function isVideoUrl(url: string): boolean {
  return /\.(mp4|webm|ogg|mov|m4v)($|\?)/i.test(url);
}

function SectionTitle({ children }: { children: string }) {
  return (
    <p className="mb-2 text-[11px] font-bold uppercase tracking-[0.12em] text-[#7A1E1E]">
      {children}
    </p>
  );
}

function CampaignStoryMediaGrid({ urls }: { urls: string[] }) {
  if (urls.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-2 gap-3">
      {urls.map(url => (
        <div
          key={url}
          className="relative aspect-[4/3] overflow-hidden rounded-xl border border-gray-100 bg-gray-50"
        >
          {isVideoUrl(url) ? (
            <video
              src={url}
              className="h-full w-full object-cover"
              controls
              muted
              playsInline
              preload="metadata"
            />
          ) : (
            <img
              src={url}
              alt=""
              className="h-full w-full object-cover"
              loading="lazy"
              referrerPolicy="no-referrer"
            />
          )}
        </div>
      ))}
    </div>
  );
}

interface DonationCampaignBeforeAfterAccordionProps {
  campaign: DonationCampaign;
}

export default function DonationCampaignBeforeAfterAccordion({
  campaign,
}: DonationCampaignBeforeAfterAccordionProps) {
  const [activePhase, setActivePhase] = useState<StoryPhase | null>(null);

  const beforeMedia = campaign.beforeMedia ?? [];
  const afterMedia = campaign.afterMedia ?? [];
  const hasBefore = Boolean(
    campaign.beforeDetails?.trim() ?? beforeMedia.length > 0
  );
  const hasAfter = Boolean(
    campaign.afterDetails?.trim() ?? afterMedia.length > 0
  );

  if (!hasBefore && !hasAfter) {
    return null;
  }

  const phaseContent =
    activePhase === "before"
      ? {
          title: "Before Details",
          subtitle: "The situation before your support",
          details: campaign.beforeDetails,
          media: beforeMedia,
        }
      : activePhase === "after"
        ? {
            title: "After Details",
            subtitle: "The change your donation helps create",
            details: campaign.afterDetails,
            media: afterMedia,
          }
        : null;

  return (
    <>
      <div className="rounded-2xl border border-gray-100/80 bg-white p-6 shadow-[0_4px_24px_rgba(122,30,30,0.06)]">
        <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.12em] text-gray-400">
          Campaign story
        </p>
        <p className="mb-4 text-xs text-gray-500">
          See the situation before support and the impact after your donation.
        </p>

        <div
          className={`grid gap-3 ${hasBefore && hasAfter ? "grid-cols-2" : "grid-cols-1"}`}
        >
          {hasBefore && (
            <Button
              type="button"
              variant="outline"
              className="h-auto min-h-[48px] flex-col items-start gap-1 rounded-xl border-[#7A1E1E]/25 bg-white px-3 py-3.5 text-left shadow-sm hover:border-[#7A1E1E]/45 hover:bg-[#FFF9F0]"
              onClick={() => setActivePhase("before")}
            >
              <span className="flex w-full items-center justify-between gap-2">
                <span className="text-sm font-semibold text-[#7A1E1E]">
                  Before
                </span>
                <ArrowRight className="h-4 w-4 shrink-0 text-[#D4A017]" />
              </span>
            </Button>
          )}
          {hasAfter && (
            <Button
              type="button"
              variant="outline"
              className="h-auto min-h-[48px] flex-col items-start gap-1 rounded-xl border-[#7A1E1E]/25 bg-white px-3 py-3.5 text-left shadow-sm hover:border-[#7A1E1E]/45 hover:bg-[#FFF9F0]"
              onClick={() => setActivePhase("after")}
            >
              <span className="flex w-full items-center justify-between gap-2">
                <span className="text-sm font-semibold text-[#7A1E1E]">
                  After (Impact)
                </span>
                <ArrowRight className="h-4 w-4 shrink-0 text-[#D4A017]" />
              </span>
            </Button>
          )}
        </div>
      </div>

      <Dialog
        open={activePhase !== null}
        onOpenChange={open => {
          if (!open) {
            setActivePhase(null);
          }
        }}
      >
        <DialogContent
          overlayClassName="bg-black/50 backdrop-blur-sm"
          className="flex h-[min(640px,95vh)] w-full max-w-[calc(100%-2rem)] flex-col gap-0 overflow-hidden rounded-2xl border border-gray-100/90 bg-[#FFF9F0] p-0 shadow-2xl sm:max-w-[620px] [&>button]:hidden"
        >
          {phaseContent && (
            <>
              <div className="relative shrink-0 border-b border-gray-100 bg-white px-6 pb-4 pr-14 pt-6 shadow-sm">
                <button
                  type="button"
                  onClick={() => setActivePhase(null)}
                  className="absolute right-4 top-4 rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-[#7A1E1E]"
                  aria-label="Close"
                >
                  <X className="h-6 w-6" />
                </button>
                <DialogHeader className="space-y-1.5 text-left">
                  <DialogTitle className="text-2xl font-semibold tracking-tight text-[#7A1E1E]">
                    {phaseContent.title}
                  </DialogTitle>
                  <DialogDescription className="text-sm text-gray-500">
                    {phaseContent.subtitle}
                  </DialogDescription>
                </DialogHeader>
              </div>

              <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-6 py-5">
                <section>
                  <SectionTitle>Description</SectionTitle>
                  {phaseContent.details?.trim() ? (
                    <p className="rounded-xl border border-gray-100 bg-white px-4 py-3 text-sm leading-relaxed text-gray-700">
                      {phaseContent.details.trim()}
                    </p>
                  ) : (
                    <p className="rounded-xl border border-dashed border-gray-200 bg-white px-4 py-3 text-sm text-gray-500">
                      No description available for this section.
                    </p>
                  )}
                </section>

                <section className="mt-6">
                  <SectionTitle>Images</SectionTitle>
                  {phaseContent.media.length > 0 ? (
                    <CampaignStoryMediaGrid urls={phaseContent.media} />
                  ) : (
                    <p className="rounded-xl border border-dashed border-gray-200 bg-white px-4 py-8 text-center text-sm text-gray-500">
                      No images available for this section.
                    </p>
                  )}
                </section>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
